import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BaseComponent } from 'src/app/common/base/base.component';
import { Resource } from 'src/app/components/catalogs/beamline-catalog/resource/resources';
import { Kind, RSF, RSFAmendment, RSFOtherSystem, RSFResourceRelation, RSFResourceRelationType } from '../../../rsf';
import { Field, RsfService, RSFStatus } from '../../../rsf.service';
import { debounceTime, distinctUntilChanged, Subscription, takeUntil } from 'rxjs';
import { RSFResourceTypesSetting, RSFSettings, RSFSettingType } from '../../../rsf-settings/rsf-settings';
import { MatChipInputEvent } from '@angular/material/chips';
import { LiveAnnouncer } from '@angular/cdk/a11y';

@Component({
  selector: 'rsf-main-s1-systems-affected',
  templateUrl: './rsf-main-s1-systems-affected.component.html',
  styleUrls: ['./rsf-main-s1-systems-affected.component.scss']
})
export class RsfMainS1SystemsAffectedComponent extends BaseComponent implements OnChanges {
  @Input() rsfProxy?: RSF | RSFAmendment | null;
  @Input() rsfAmendment?: RSFAmendment | null;
  @Input() disabled?: boolean;
  @Input() hasErrors?: boolean;
  @Input() checkDiff!: (field: Field) => boolean;
  @Input() createResourceRelations!: (resources: Resource[], type: RSFResourceRelationType) => (RSFResourceRelation)[];
  @Input() beamlineLocations?: Resource[];
  @Input() acceleratorLocations?: Resource[];
  @Input() status?: RSFStatus;
  @Input() service?: RsfService;

  @Output() changed = new EventEmitter<RSF | RSFAmendment>();
  @Output() locationAddedID = new EventEmitter<number>();

  announcer = inject(LiveAnnouncer);

  field = Field;
  rsf?: RSF;

  ctrlBeamline = new FormControl();
  ctrlBeamlineLocations = new FormControl();
  ctrlAccelerator = new FormControl();
  ctrlAcceleratorLocations = new FormControl();
  ctrlShielding = new FormControl();
  ctrlShlRemoveReplace = new FormControl();
  ctrlShlModification = new FormControl();
  ctrlShlModificationARC = new FormControl();
  ctrlShlModificationBRC = new FormControl();
  ctrlPPS = new FormControl();
  ctrlPPSMaintenance = new FormControl();
  ctrlPPSModification = new FormControl();
  ctrlPPSModificationARC = new FormControl();
  ctrlPPSModificationBRC = new FormControl();
  ctrlPPSOther = new FormControl();
  ctrlPPSOtherText = new FormControl();
  ctrlPPSAcis = new FormControl();
  ctrlTombis = new FormControl();
  ctrlPPSPLCSet = new FormControl();
  ctrlPPSSoftware = new FormControl();
  ctrlETIAccess = new FormControl();
  ctrlTOMBISPLCSet = new FormControl();
  ctrlTOMBISSoftware = new FormControl();
  ctrlTopOffInterlock = new FormControl();
  ctrlRadMonitors = new FormControl();
  ctrlRadMonitorsMoving = new FormControl();
  ctrlRadMonitorsDisconnecting = new FormControl();
  ctrlRadMonitorsModifying = new FormControl();
  ctrlRadMonitorsTripPoint = new FormControl();
  ctrlRadSurvey = new FormControl();
  ctrlOtherSystem = new FormControl();
  ctrlOtherSystems = new FormControl();

  hasShlErrors?: boolean;

  prevLocations: (RSFResourceRelation)[] = [];

  resourceTypesSettings?: RSFResourceTypesSetting[] = [];

  rsfSettings?: RSFSettings[];
  rsfSettingsSubs!: Subscription;

  beamlineResourceTypeIDs: number[] = [];
  acceleratorResourceTypeIDs: number[] = [];

  beamlineLabel?: string;
  acceleratorLabel?: string;
  otherSystemsLabel?: string = 'Other Systems';

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.status?.isLogView) {
      // History rendering is read-only; avoid changing control errors during Angular checks.
      this.validateBeamlineLocations();
      this.validateAcceleratorLocations();
      this.validateOtherSystems();
    }

    this.rsf = this.rsfAmendment?.rsf;
    this.loadRSFSettings();
  }

  loadRSFSettings() {
    this.rsfSettingsSubs = this.store.select(state => state.RSFSettings.data).pipe(takeUntil(this.destroy$), debounceTime(500),
      distinctUntilChanged((prev, curr) => this.quickHash(prev) === this.quickHash(curr))).subscribe((data: RSFSettings[]) => {
        if (data?.length) {
          this.resourceTypesSettings = [];
          this.rsfSettings = data.filter(s => s.type == RSFSettingType.ResourceTypes);
          this.rsfSettings.forEach(s => {
            const value = this.utils.JSONparse(s.defaultValue) as RSFResourceTypesSetting;
            const rt = {} as RSFResourceTypesSetting;
            rt.id = s.id;
            rt.name = value?.name ?? s.name;
            rt.description = value?.description ?? s.description;
            rt.type = s.type;
            rt.order = value?.order ?? s.order ?? 0;
            rt.resourceTypes = value?.resourceTypes;
            rt.conditions = value?.conditions;
            this.resourceTypesSettings?.push(rt);
          });
          this.resourceTypesSettings?.sort((a, b) => this.utils.sort(a.order, b.order));
          this.beamlineResourceTypeIDs = this.resourceTypesSettings?.[0]?.resourceTypes?.map(r => r.id).flat() ?? [];
          this.acceleratorResourceTypeIDs = this.resourceTypesSettings?.[1]?.resourceTypes?.map(r => r.id).flat() ?? [];
          this.beamlineLabel = this.resourceTypesSettings?.[0]?.name;
          this.acceleratorLabel = this.resourceTypesSettings?.[1]?.name;
        }
      });
  }

  /**
   * Synchronize system-affected form control values into the RSF proxy and enforce dependent control states.
   * Clear incompatible child selections when parent toggles are off, then emit the updated model for downstream consumers.
   * Mutate both form controls and rsfProxy fields as a side effect.
   */
  onSystemChanged() {
    // Disabled controls can emit while Angular initializes; never copy them into a Minor Amendment.
    if (this.status?.isMinorAmendment) {
      return;
    }

    if (this.rsfProxy) {
      // Copy top-level control values into the backing RSF proxy model.
      this.rsfProxy.beamLine = this.ctrlBeamline.value;
      if (!this.rsfProxy.beamLine) {
        this.rsfProxy.rsfResourceRelations = this.rsfProxy.rsfResourceRelations.filter(rr => rr.type != RSFResourceRelationType.Beamline);
        this.beamlineLocations = [];
      }
      this.rsfProxy.accelerator = this.ctrlAccelerator.value;
      if (!this.rsfProxy.accelerator) {
        this.rsfProxy.rsfResourceRelations = this.rsfProxy.rsfResourceRelations.filter(rr => rr.type != RSFResourceRelationType.Accelerator);
        this.acceleratorLocations = [];
      }
      this.rsfProxy.shielding = this.ctrlShielding.value;
      this.rsfProxy.shieldingRemoveReplace = this.ctrlShlRemoveReplace.value;
      this.rsfProxy.shieldingModification = this.ctrlShlModification.value;
      this.rsfProxy.shieldingModificationARC = this.ctrlShlModificationARC.value && this.ctrlShlModification.value;
      this.rsfProxy.shieldingModificationBRC = this.ctrlShlModificationBRC.value && this.ctrlShlModification.value;
      this.rsfProxy.pps = this.ctrlPPS.value;
      this.rsfProxy.ppsMaintenance = this.ctrlPPSMaintenance.value;
      this.rsfProxy.ppsModification = this.ctrlPPSModification.value;
      this.rsfProxy.ppsModificationARC = this.ctrlPPSModificationARC.value && this.ctrlPPSModification.value;
      this.rsfProxy.ppsModificationBRC = this.ctrlPPSModificationBRC.value && this.ctrlPPSModification.value;
      this.rsfProxy.ppsAcis = this.ctrlPPSAcis.value;
      this.rsfProxy.tombis = this.ctrlTombis.value;
      this.rsfProxy.ppsOther = this.ctrlPPSOther.value;
      this.rsfProxy.ppsOtherText = this.ctrlPPSOtherText.value;
      this.rsfProxy.radMonitors = this.ctrlRadMonitors.value;
      this.rsfProxy.radMonitorsMoving = this.ctrlRadMonitorsMoving.value;
      this.rsfProxy.radMonitorsDisconnecting = this.ctrlRadMonitorsDisconnecting.value;
      this.rsfProxy.radMonitorsModifying = this.ctrlRadMonitorsModifying.value;
      this.rsfProxy.radMonitorsTripPoint = this.ctrlRadMonitorsTripPoint.value;
      this.rsfProxy.ppsplcSet = this.ctrlPPSPLCSet.value;
      this.rsfProxy.ppsSoftware = this.ctrlPPSSoftware.value;
      this.rsfProxy.etiAccess = this.ctrlETIAccess.value;
      this.rsfProxy.tombisplcSet = this.ctrlTOMBISPLCSet.value;
      this.rsfProxy.tombisSoftware = this.ctrlTOMBISSoftware.value;
      this.rsfProxy.topOffInterlockSetPoint = this.ctrlTopOffInterlock.value;
      this.rsfProxy.radSurveyRequired = this.ctrlRadSurvey.value;
      this.rsfProxy.otherSystems = this.ctrlOtherSystem.value;

      if (!this.ctrlPPS.value) {
        // Clearing PPS must also clear every dependent selection so hidden controls cannot be persisted.
        const ppsChildControls = [
          this.ctrlPPSMaintenance,
          this.ctrlPPSModification,
          this.ctrlPPSModificationARC,
          this.ctrlPPSModificationBRC,
          this.ctrlPPSAcis,
          this.ctrlTombis,
          this.ctrlPPSOther,
          this.ctrlRadMonitors,
          this.ctrlRadMonitorsMoving,
          this.ctrlRadMonitorsDisconnecting,
          this.ctrlRadMonitorsModifying,
          this.ctrlRadMonitorsTripPoint,
          this.ctrlPPSPLCSet,
          this.ctrlPPSSoftware,
          this.ctrlETIAccess,
          this.ctrlTOMBISPLCSet,
          this.ctrlTOMBISSoftware,
          this.ctrlTopOffInterlock,
        ];

        ppsChildControls.forEach(control => control.setValue(false, { emitEvent: false }));
        this.ctrlPPSOtherText.setValue('', { emitEvent: false });

        this.rsfProxy.ppsMaintenance = false;
        this.rsfProxy.ppsModification = false;
        this.rsfProxy.ppsModificationARC = false;
        this.rsfProxy.ppsModificationBRC = false;
        this.rsfProxy.ppsAcis = false;
        this.rsfProxy.tombis = false;
        this.rsfProxy.ppsOther = false;
        this.rsfProxy.ppsOtherText = '';
        this.rsfProxy.radMonitors = false;
        this.rsfProxy.radMonitorsMoving = false;
        this.rsfProxy.radMonitorsDisconnecting = false;
        this.rsfProxy.radMonitorsModifying = false;
        this.rsfProxy.radMonitorsTripPoint = false;
        this.rsfProxy.ppsplcSet = false;
        this.rsfProxy.ppsSoftware = false;
        this.rsfProxy.etiAccess = false;
        this.rsfProxy.tombisplcSet = false;
        this.rsfProxy.tombisSoftware = false;
        this.rsfProxy.topOffInterlockSetPoint = false;
      }

      // Gate monitor-moving/disconnecting options by PPS maintenance toggle.
      if (this.ctrlPPSMaintenance.value) {
        this.ctrlRadMonitorsMoving.enable();
        this.ctrlRadMonitorsDisconnecting.enable();
      }
      else {
        this.ctrlRadMonitorsMoving.disable();
        this.ctrlRadMonitorsMoving.setValue(false);
        this.rsfProxy.radMonitorsMoving = false;
        this.ctrlRadMonitorsDisconnecting.disable();
        this.ctrlRadMonitorsDisconnecting.setValue(false);
        this.rsfProxy.radMonitorsDisconnecting = false;
      }

      // Gate monitor-modifying/trip-point options by PPS modification toggle.
      if (this.ctrlPPSModification.value) {
        this.ctrlRadMonitorsModifying.enable();
        this.ctrlRadMonitorsTripPoint.enable();
      }
      else {
        this.ctrlRadMonitorsModifying.disable();
        this.ctrlRadMonitorsModifying.setValue(false);
        this.rsfProxy.radMonitorsModifying = false;
        this.ctrlRadMonitorsTripPoint.disable();
        this.ctrlRadMonitorsTripPoint.setValue(false);
        this.rsfProxy.radMonitorsTripPoint = false;
      }

      // Enable or clear free-text other-systems values based on parent toggle.
      if (this.ctrlOtherSystem.value) {
        this.ctrlOtherSystems.enable();
      }
      else {
        this.ctrlOtherSystems.setValue(null);
        this.rsfProxy.rsfOtherSystems = [];
        this.ctrlOtherSystems.disable();
      }

      // Keep shielding child toggles consistent with parent shielding/modification choices.
      if (this.ctrlShielding.value) {
        this.ctrlShlRemoveReplace.enable();
        this.ctrlShlModification.enable();
        if (this.ctrlShlModification.value) {
          this.ctrlShlModificationARC.enable();
          this.ctrlShlModificationBRC.enable();
        }
        else {
          this.ctrlShlModificationARC.disable();
          this.ctrlShlModificationARC.setValue(false);
          this.ctrlShlModificationBRC.disable();
          this.ctrlShlModificationBRC.setValue(false);
        }
      }
      else {
        this.rsfProxy.shielding = false;

        this.ctrlShlRemoveReplace.disable();
        this.ctrlShlRemoveReplace.setValue(false);
        this.rsfProxy.shieldingRemoveReplace = false;

        this.ctrlShlModification.disable();
        this.ctrlShlModification.setValue(false);
        this.rsfProxy.shieldingModification = false;

        this.ctrlShlModificationARC.disable();
        this.ctrlShlModificationARC.setValue(false);
        this.rsfProxy.shieldingModificationARC = false;

        this.ctrlShlModificationBRC.disable();
        this.ctrlShlModificationBRC.setValue(false);
        this.rsfProxy.shieldingModificationBRC = false;
      }

      // Notify parent listeners that system-affected values have changed.
      this.changed.emit(this.rsfProxy);
    }
  }

  /**
   * Handles changes to the selected beamline locations.
   *
   * Updates the `beamlineLocations` property with the new selection,
   * ensures the `rsfResourceRelations` array is initialized,
   * replaces any existing beamline relations with new ones based on the selection,
   * and emits the updated `rsf` object.
   *
   * @param e - The array of selected `Resource` objects representing beamline locations.
   */
  onBeamlineLocationsChanged(e: Resource[]) {
    // Beamline locations are restricted during Minor Amendments.
    if (this.status?.isMinorAmendment) {
      return;
    }

    this.prevLocations = this.rsfProxy!.rsfResourceRelations;
    this.beamlineLocations = e;
    if (!this.rsfProxy?.rsfResourceRelations) this.rsfProxy!.rsfResourceRelations = [];
    this.rsfProxy!.rsfResourceRelations = this.rsfProxy!.rsfResourceRelations.filter(r => r.type != RSFResourceRelationType.Beamline).concat(this.createResourceRelations(e, RSFResourceRelationType.Beamline));
    this.validateBeamlineLocations();
    this.checkAddedLocation();
    this.changed.emit(this.rsfProxy!);
  }

  /**
   * Handles changes to the selected accelerator locations.
   *
   * Updates the `acceleratorLocations` property with the new selection,
   * ensures the `rsfResourceRelations` array exists, removes any existing
   * accelerator relations, and adds new relations for the selected resources.
   * Emits the updated `rsf` object via the `changed` event.
   *
   * @param e - The array of selected accelerator `Resource` objects.
   */
  onAcceleratorLocationsChanged(e: Resource[]) {
    // Accelerator locations are restricted during Minor Amendments.
    if (this.status?.isMinorAmendment) {
      return;
    }

    this.prevLocations = this.rsfProxy!.rsfResourceRelations;
    this.acceleratorLocations = e;
    if (!this.rsfProxy?.rsfResourceRelations) this.rsfProxy!.rsfResourceRelations = [];
    this.rsfProxy!.rsfResourceRelations = this.rsfProxy!.rsfResourceRelations.filter(r => r.type !== RSFResourceRelationType.Accelerator).concat(this.createResourceRelations(e, RSFResourceRelationType.Accelerator));
    this.validateAcceleratorLocations();
    this.checkAddedLocation();
    this.changed.emit(this.rsfProxy!);
  }

  /**
   * Handles changes to the radiation monitors form controls.
   *
   * Updates the corresponding properties (`radMonitors`, `radMonitorsMoving`,
   * `radMonitorsDisconnecting`, `radMonitorsModifying`) of the `rsf` object
   * with the current values from their respective form controls.
   * Emits the updated `rsf` object via the `changed` event emitter.
   */
  onRadMonitorsChanged() {
    this.rsfProxy!.radMonitors = this.ctrlRadMonitors.value;
    this.rsfProxy!.radMonitorsMoving = this.ctrlRadMonitorsMoving.value;
    this.rsfProxy!.radMonitorsDisconnecting = this.ctrlRadMonitorsDisconnecting.value;
    this.rsfProxy!.radMonitorsModifying = this.ctrlRadMonitorsModifying.value;
    this.rsfProxy!.radMonitorsTripPoint = this.ctrlRadMonitorsTripPoint.value;
    this.changed.emit(this.rsfProxy!);
  }

  /**
   * Enforce that at least one beamline location is selected when beamline is enabled.
   * Mark the control touched on failure so required validation is surfaced in the UI.
   * Update only control error state as a side effect.
   */
  validateBeamlineLocations() {
    // Consider the field valid when beamline is disabled, or when enabled with at least one location.
    const valid = this.rsfProxy?.beamLine && !!this.beamlineLocations?.length || !this.rsfProxy?.beamLine;
    if (!valid) {
      // Set required error and force touched state to display validation feedback.
      this.ctrlBeamlineLocations.setErrors({ required: true });
      this.ctrlBeamlineLocations.markAsTouched();
    }
    else {
      // Clear stale validation errors once the selection becomes valid.
      this.ctrlBeamlineLocations.setErrors(null);
    }
  }

  /**
   * Enforce that at least one accelerator location is selected when accelerator is enabled.
   * Mark the control touched on failure so required validation is surfaced in the UI.
   * Update only control error state as a side effect.
   */
  validateAcceleratorLocations() {
    // Consider the field valid when accelerator is disabled, or when enabled with at least one location.
    const valid = this.rsfProxy?.accelerator && !!this.acceleratorLocations?.length || !this.rsfProxy?.accelerator;
    if (!valid) {
      // Set required error and force touched state to display validation feedback.
      this.ctrlAcceleratorLocations.setErrors({ required: true });
      this.ctrlAcceleratorLocations.markAsTouched();
    }
    else {
      // Clear stale validation errors once the selection becomes valid.
      this.ctrlAcceleratorLocations.setErrors(null);
    }
  }

  /**
   * Enforce that at least one other-system entry exists when the other-systems toggle is enabled.
   * Set validation errors for both controls and keep dependent input enabled state aligned to the toggle.
   * Mutate control error, touched, and enabled/disabled state as a side effect.
   */
  validateOtherSystems() {
    // Consider valid when toggle is off, or when enabled with at least one value.
    const valid = this.rsfProxy?.otherSystems && !!this.rsfProxy.rsfOtherSystems?.length || !this.rsfProxy?.otherSystems;
    if (!valid) {
      // Surface required validation on both toggle and values controls.
      this.ctrlOtherSystem.setErrors({ required: true });
      this.ctrlOtherSystems.setErrors({ required: true });
      this.ctrlOtherSystems.markAsTouched();
    }
    else {
      // Clear stale required errors once selection becomes valid.
      this.ctrlOtherSystem.setErrors(null);
      this.ctrlOtherSystems.setErrors(null);
    }

    // Keep the details control availability in sync with the parent toggle.
    if (this.ctrlOtherSystem.value) {
      this.ctrlOtherSystems.enable();
    }
    else {
      this.ctrlOtherSystems.disable();
    }
  }

  /**
   * Detect whether exactly one resource relation was added and emit its resource ID when found.
   * Resynchronize the previous-relations snapshot when relation counts diverge by more than one.
   * Mutate prevLocations and emit locationAddedID as side effects.
   */
  checkAddedLocation(): void {
    // Compare current relations with the previous snapshot.
    const arr = this.rsfProxy?.rsfResourceRelations ?? [];
    if (arr.length === this.prevLocations.length + 1) {
      // Identify the newly added relation by resource ID and emit it.
      const added = arr.find(x => !this.prevLocations.some(y => y.resourceID === x.resourceID));
      if (added) {
        this.prevLocations.push(added);
        this.locationAddedID.emit(added.resourceID);
      }
    }
    else if (arr.length !== this.prevLocations.length) {
      // Reset snapshot when changes are not a single-item addition.
      this.prevLocations = [...arr];
    }

  }

  /**
   * Add a normalized other-system entry to the RSF proxy when the value is non-empty and unique.
   * Build the payload shape from the current proxy kind and emit the updated model after insertion.
   * Mutate rsfProxy.rsfOtherSystems and emit changed as side effects.
   * @param value User-provided other-system label to persist.
   */
  addOtherSystemValue(value: string): void {
    let os;
    // Ensure the collection exists before duplicate checks and insertion.
    if (this.rsfProxy && !this.rsfProxy?.rsfOtherSystems) this.rsfProxy.rsfOtherSystems = [];
    // Prevent case-insensitive duplicate labels.
    const exists = this.rsfProxy?.rsfOtherSystems?.some(
      x => x.description?.toLowerCase() === value.toLowerCase()
    );

    // Create and append a kind-specific entry only when input is valid and new.
    if (value && !exists) {
      switch (this.rsfProxy?.kind) {
        case Kind.RSF:
          os = { rsfID: this.rsfProxy.id, description: value } as RSFOtherSystem;
          break;
        case Kind.RSFAmendment:
          os = { rsfAmendmentID: this.rsfProxy.id, description: value } as RSFOtherSystem;
          break;
      }
      this.rsfProxy?.rsfOtherSystems.push(os!);
      this.changed.emit(this.rsfProxy!);
    }
  }

  /**
   * Handle chip-input submission for other systems and delegate persistence to addOtherSystemValue.
   * Ignore blank input and clear the chip field after processing.
   * Mutate input/chip UI state as a side effect.
   * @param event Chip input event containing the raw text and input handle.
   */
  addOtherSystem(event: MatChipInputEvent): void {
    const input = event.input;
    // Normalize user input before validation and insert.
    const value = (event.value || '').trim();

    if (!value) return;

    // Reuse shared insertion and duplicate-guard logic.
    this.addOtherSystemValue(value);

    // Clear native input value to prevent stale text in the field.
    if (input) {
      input.value = '';
    }

    // Clear chip input control state as an additional safety reset.
    event.chipInput?.clear();
  }

  /**
   * Handle blur-based free-text entry for other systems and delegate persistence to addOtherSystemValue.
   * Ignore blank input and clear the source input after processing.
   * Mutate input UI state as a side effect.
   * @param input Text input element that triggered blur submission.
   */
  addOtherSystemFromBlur(input: HTMLInputElement): void {
    // Normalize blur text before validation and insert.
    const value = input.value?.trim();

    if (!value) return;

    // Reuse shared insertion and duplicate-guard logic.
    this.addOtherSystemValue(value);

    // Clear input after handling to avoid duplicate blur submissions.
    input.value = '';
  }

  /**
   * Remove an existing other-system entry when present, then notify accessibility and parent listeners.
   * Perform no action when the entry is not found.
   * Mutate rsfProxy.rsfOtherSystems and emit announce/changed side effects.
   * @param os Other-system entry to remove from the proxy collection.
   */
  removeOtherSystem(os: RSFOtherSystem) {
    // Locate the exact entry instance in the current collection.
    const index = this.rsfProxy?.rsfOtherSystems.indexOf(os) ?? -1;
    if (index >= 0) {
      // Remove the entry and notify assistive technology and parent observers.
      this.rsfProxy?.rsfOtherSystems.splice(index, 1);

      this.announcer.announce(`removed ${os.description}`);
      this.changed.emit(this.rsfProxy!);
    }
  }


}
